import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import {
  type InstagramAudioResult,
  type InstagramLookup,
  type InstagramPlaceResult,
  instagramLookupService,
} from '@/services/data/InstagramLookupService';

function unwrap<T>(response: {
  data?: InstagramLookup<T> | null;
  error?: { message: string } | null;
}) {
  if (response.error || !response.data) {
    throw new Error(response.error?.message ?? 'The search failed');
  }
  return response.data;
}

/** Instagram licensed audio search. With no query Meta returns trending tracks. */
export function useInstagramAudioSearch(
  query: string,
  audioType: 'music' | 'original_sound',
  enabled: boolean,
) {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ['instagram-audio-search', user?.id, audioType, query],
    queryFn: async (): Promise<InstagramLookup<InstagramAudioResult>> =>
      unwrap(await instagramLookupService.searchAudio(query, audioType)),
    enabled: enabled && !!user?.id && !loading,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useInstagramPlaceSearch(query: string, enabled: boolean) {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ['instagram-place-search', user?.id, query],
    queryFn: async (): Promise<InstagramLookup<InstagramPlaceResult>> =>
      unwrap(await instagramLookupService.searchPlaces(query)),
    enabled: enabled && query.trim().length >= 2 && !!user?.id && !loading,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
